import { h } from 'preact';

interface ProductPricingProps {
  price: number;
  originalPrice?: number;
  discount?: string;
}

// Format price with commas for 4+ digits
const formatPrice = (price: number): string => {
  return price.toLocaleString('en-US');
};

/**
 * ProductPricing - Displays product pricing with discount
 * 
 * **Features:**
 * - Shows "From" prefix with current price
 * - Displays original price with strikethrough
 * - Shows discount badge if available
 * - Automatically formats prices with commas for 4+ digits
 * 
 * **Display:**
 * - Current price in large, bold text
 * - Original price in gray with strikethrough
 * - Discount badge in red with white text
 * - Uses semantic footer tag
 * 
 * @param price - Current price number
 * @param originalPrice - Original price number (optional)
 * @param discount - Discount text (e.g., "SAVE 6%")
 * @returns JSX element
 */
const ProductPricing = ({ price, originalPrice, discount }: ProductPricingProps) => {
  return (
    <footer className="flex justify-between items-center">
      <div>
        <span className="text-giftory-midnight text-xs md:text-sm font-medium font-inter-tight leading-[22px] md:leading-normal">From </span>
        <span className="text-giftory-midnight text-lg md:text-xl font-extrabold font-inter-tight leading-[22px] md:leading-normal">
          ${formatPrice(price)}
        </span>
        {originalPrice && originalPrice !== price && (
          <span className="text-neutral-100 text-xs md:text-sm font-medium font-inter-tight line-through leading-[22px] md:leading-normal pl-1 italic">
            ${formatPrice(originalPrice)}
          </span>
        )}
      </div>

      {discount && (
        <div className="flex items-start">
          <div className="px-2 py-1.5 bg-[#E30016] rounded-lg flex justify-center items-center gap-1">
            <span className="text-center text-white text-xs font-medium font-inter-tight uppercase leading-none">{discount}</span>
          </div>
        </div>
      )}
    </footer>
  );
};

export default ProductPricing; 